import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MapPinArea, PlayCircle, StopCircle, WarningCircle, Receipt, MapTrifold, X } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import RouteMapModal from "../components/RouteMapModal";

const MODES = [
  { v: "bike", l: "Bike" },
  { v: "car", l: "Car" },
  { v: "auto", l: "Auto" },
  { v: "cab", l: "Cab" },
  { v: "public", l: "Public" },
];

// Ping cadence for live waypoint drops during an active trip
const WAYPOINT_PING_MS = 60_000;

export default function Trips() {
  const { user } = useAuth();  // eslint-disable-line no-unused-vars
  const [active, setActive] = useState(null);
  const [trips, setTrips] = useState([]);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ mode: "bike", purpose: "", vehicle_no: "", project_id: "" });
  const [projects, setProjects] = useState([]);
  const [purposes, setPurposes] = useState([]);
  const [mapTrip, setMapTrip] = useState(null);
  const pingTimerRef = useRef(null);
  const lastPingRef = useRef(null);

  const load = () => {
    api.get("/trips/active").then(({ data }) => setActive(data)).catch(() => {});
    api.get("/trips/mine", { params: { limit: 30 } }).then(({ data }) => setTrips(data)).catch(() => {});
    api.get("/trips/monthly-summary").then(({ data }) => setSummary(data)).catch(() => {});
    api.get("/projects").then(({ data }) => setProjects(data)).catch(() => setProjects([]));
    api.get("/trip-purposes").then(({ data }) => setPurposes(data.filter((p) => p.active !== false))).catch(() => setPurposes([]));
  };
  useEffect(() => { load(); }, []);

  // Live waypoint pinger — drops a GPS point every 60 s while a trip is active.
  useEffect(() => {
    if (pingTimerRef.current) { clearInterval(pingTimerRef.current); pingTimerRef.current = null; }
    if (!active?.id) return;
    const ping = () => {
      if (!navigator.geolocation) return;
      navigator.geolocation.getCurrentPosition(
        (p) => {
          const lat = p.coords.latitude, lng = p.coords.longitude;
          const last = lastPingRef.current;
          // Only ping if we've moved > ~10 m to avoid spamming when stationary
          if (last && Math.abs(last.lat - lat) < 0.0001 && Math.abs(last.lng - lng) < 0.0001) return;
          lastPingRef.current = { lat, lng };
          api.post(`/trips/${active.id}/waypoint`, { lat, lng }).catch(() => {});
        },
        () => {},
        { enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 },
      );
    };
    ping();
    pingTimerRef.current = setInterval(ping, WAYPOINT_PING_MS);
    return () => { if (pingTimerRef.current) clearInterval(pingTimerRef.current); pingTimerRef.current = null; };
  }, [active?.id]);

  const getLocation = () => new Promise((res, rej) => {
    if (!navigator.geolocation) return rej(new Error("Geolocation not supported"));
    navigator.geolocation.getCurrentPosition(
      (p) => res({ lat: p.coords.latitude, lng: p.coords.longitude }),
      (e) => rej(e),
      { enableHighAccuracy: true, timeout: 15000 },
    );
  });

  const startTrip = async () => {
    setBusy(true);
    try {
      const loc = await getLocation();
      const payload = { lat: loc.lat, lng: loc.lng, mode: form.mode,
        purpose: form.purpose || null, vehicle_no: form.vehicle_no || null,
        project_id: form.project_id || null };
      await api.post("/trips/start", payload);
      toast.success("Trip started · we'll drop a GPS waypoint every minute automatically");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const endTrip = async () => {
    if (!active) return;
    setBusy(true);
    try {
      const loc = await getLocation();
      const { data } = await api.post(`/trips/${active.id}/end`, { lat: loc.lat, lng: loc.lng });
      toast.success(`Trip ended · ${data.distance_km} km · ₹${data.suggested_amount || 0}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const convertToClaim = async (id) => {
    try {
      await api.post(`/trips/${id}/to-claim`, {});
      toast.success("Fuel claim drafted — attach a receipt from Reimbursements → Draft claims");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-zenith-teal">Field ops</div>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-adb-navy">GPS trip tracking</h1>
        <p className="text-sm text-zinc-500 mt-2">
          Start a trip when you set off, end it when you arrive — we drop a GPS waypoint every 60 s, compute road distance, and flag anomalies automatically.
          Fuel reimbursements auto-fill from your trips.
        </p>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Trips this month" value={summary.trip_count} />
          <StatCard label="Total km" value={`${summary.total_km} km`} />
          <StatCard label="Amount (est.)" value={`₹ ${Number(summary.total_amount).toLocaleString("en-IN")}`} />
          <StatCard
            label={summary.cap ? `Cap used` : `No monthly cap`}
            value={summary.cap ? `${summary.cap_used_pct ?? 0}% of ${summary.cap} km` : "—"}
            danger={summary.cap_used_pct && summary.cap_used_pct > 100}
          />
        </div>
      )}

      {active ? (
        <div className="border border-zenith-teal/40 bg-zenith-teal/5 rounded-sm p-5" data-testid="active-trip">
          <div className="flex items-start gap-3">
            <PlayCircle size={26} weight="fill" className="text-zenith-teal flex-shrink-0" />
            <div className="flex-1">
              <div className="font-display font-bold text-adb-navy">Trip in progress · {active.mode.toUpperCase()}</div>
              <div className="text-xs text-zinc-600 mt-1 flex items-start gap-1"><MapPinArea size={12} className="mt-0.5" /> {active.start_address || `${active.start_lat}, ${active.start_lng}`}</div>
              <div className="text-xs text-zinc-500 mt-1">Started {new Date(active.start_at).toLocaleString()} · {active.purpose || "no purpose set"}</div>
              <div className="text-[11px] text-zenith-teal mt-1 font-semibold inline-flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-zenith-teal animate-pulse" /> Live GPS pinging every 60 s · waypoints so far: {(active.waypoints || []).length}
              </div>
            </div>
            <Button onClick={endTrip} disabled={busy} className="bg-zenith-magenta hover:brightness-110 text-white rounded-sm" data-testid="trip-end">
              <StopCircle size={16} className="mr-1.5" weight="fill" /> End trip
            </Button>
          </div>
        </div>
      ) : (
        <div className="border border-zinc-200 bg-white rounded-sm p-5" data-testid="trip-start-form">
          <div className="font-display font-bold text-adb-navy mb-3">Start a new trip</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">Mode</Label>
              <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
                <SelectTrigger className="rounded-sm h-9"><SelectValue /></SelectTrigger>
                <SelectContent>{MODES.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Vehicle no.</Label>
              <Input value={form.vehicle_no} onChange={(e) => setForm({ ...form, vehicle_no: e.target.value })} placeholder="MH12AB1234" className="rounded-sm h-9" />
            </div>
            <div>
              <Label className="text-xs">Project (optional)</Label>
              <Select value={form.project_id || "none"} onValueChange={(v) => setForm({ ...form, project_id: v === "none" ? "" : v })}>
                <SelectTrigger className="rounded-sm h-9"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— none —</SelectItem>
                  {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Purpose</Label>
              {purposes.length > 0 ? (
                <Select value={form.purpose || "__custom"} onValueChange={(v) => setForm({ ...form, purpose: v === "__custom" ? "" : v })}>
                  <SelectTrigger className="rounded-sm h-9"><SelectValue placeholder="Select purpose" /></SelectTrigger>
                  <SelectContent>
                    {purposes.map((p) => <SelectItem key={p.id} value={p.label}>{p.label}</SelectItem>)}
                    <SelectItem value="__custom">— free text —</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Input value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} placeholder="Client meeting" className="rounded-sm h-9" />
              )}
            </div>
          </div>
          <Button onClick={startTrip} disabled={busy} className="mt-4 btn-zenith text-white rounded-sm" data-testid="trip-start">
            <PlayCircle size={16} className="mr-1.5" weight="fill" /> {busy ? "Locating…" : "Start trip"}
          </Button>
        </div>
      )}

      <div className="border border-zinc-200 bg-white rounded-sm p-5">
        <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Recent trips</div>
        <div className="overflow-x-auto">
          <table className="hr-table">
            <thead><tr><th>Date</th><th>From → To</th><th>Mode</th><th>Km</th><th>Amount</th><th>Anomalies</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {trips.length === 0 && <tr><td colSpan={8} className="text-center py-6 text-zinc-500 text-sm">No trips yet. Start one above.</td></tr>}
              {trips.map((t) => (
                <tr key={t.id}>
                  <td className="font-mono-plex text-xs">{(t.start_at || "").slice(0, 10)}</td>
                  <td className="text-xs max-w-[260px]">
                    <div className="truncate" title={t.start_address_full || ""}>{t.start_address || "—"}</div>
                    <div className="text-zinc-500 truncate" title={t.end_address_full || ""}>→ {t.end_address || "—"}</div>
                  </td>
                  <td className="text-xs uppercase">{t.mode}</td>
                  <td className="font-mono-plex text-xs">{t.distance_km ?? "—"}</td>
                  <td className="font-mono-plex text-xs">{t.suggested_amount != null ? `₹ ${t.suggested_amount}` : "—"}</td>
                  <td>
                    {t.anomalies?.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {t.anomalies.map((a) => (
                          <span key={a.code} className="text-[10px] px-1.5 py-0.5 bg-orange-50 border border-orange-200 text-orange-700 rounded-sm inline-flex items-center gap-1" title={a.detail}>
                            <WarningCircle size={10} weight="fill" /> {a.code.replace(/_/g, " ")}
                          </span>
                        ))}
                      </div>
                    ) : <span className="text-zinc-400 text-xs">—</span>}
                  </td>
                  <td><Badge variant="outline" className={`rounded-sm ${t.status === "claimed" ? "bg-emerald-50 border-emerald-200 text-emerald-700" : t.status === "completed" ? "bg-blue-50 border-blue-200 text-blue-700" : "bg-amber-50 border-amber-200 text-amber-700"}`}>{t.status}</Badge></td>
                  <td className="whitespace-nowrap">
                    <button
                      onClick={() => setMapTrip(t)}
                      className="text-xs px-2 py-1 border border-adb-navy/30 text-adb-navy hover:bg-adb-navy/5 rounded-sm inline-flex items-center gap-1 mr-1"
                      title="Show route on map"
                      data-testid={`trip-map-${t.id}`}
                    >
                      <MapTrifold size={12} /> Map
                    </button>
                    {t.status === "completed" && !t.claim_id && (
                      <button onClick={() => convertToClaim(t.id)} className="text-xs px-2 py-1 border border-emerald-300 text-emerald-800 hover:bg-emerald-50 rounded-sm inline-flex items-center gap-1" data-testid={`trip-to-claim-${t.id}`}>
                        <Receipt size={12} /> To claim
                      </button>
                    )}
                    {t.claim_id && <span className="text-[10px] text-zinc-500">Claim drafted</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {mapTrip && <RouteMapModal trip={mapTrip} onClose={() => setMapTrip(null)} />}
    </div>
  );
}

function StatCard({ label, value, danger }) {
  return (
    <div className={`border rounded-sm p-4 bg-white ${danger ? "border-red-300" : "border-zinc-200"}`}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-display text-2xl font-bold mt-1 ${danger ? "text-red-600" : "text-adb-navy"}`}>{value}</div>
    </div>
  );
}
